import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import c from 'classnames';
import { Toast } from '@miotech/mprod';
import { UserLanguage } from 'types';
import {
  Analytics,
  Avatar,
  getUrlToApplication,
  redirectToApplication,
  user,
  W,
} from '..';
import MioDropdown from '../MioDropdown/MioDropdown';
import MioIcon from '../MioIcon/MioIcon';
import './MainNav.styl';

interface Props {
  children?: JSX.Element;
  hideLogo?: boolean;
}

const MIOTECH_LOGO_EN =
  'https://resources.miotech.com/static/landing-page/images/brand-logo-2021-44ad14.png';
const MIOTECH_LOGO_CN =
  'https://resources.miotech.com/static/landing-page/images/brand-logo-2022-cn-c051b1.svg';

const AMI_LOGO = 'https://static.miotech.com/images/icons/ami-nav-513x160.svg';
const WEALTHLINK_LOGO =
  'https://static.miotech.com/images/icons/wln-nav-439x64.svg';
const SPREADI_LOGO =
  'https://static.miotech.com/dark/icons/application-spreadi-rectangle-848x160.svg';
const MIOAPI_LOGO =
  'https://static.miotech.com/images/icons/mioapi-488x80-dark.svg';

const UPDATE_PREFERENCE_URL = '/api/user/preference/save';

const languageList = ['en-US', 'zh-HK', 'zh-CN'];

const ProjectMap: Record<string, string> = {
  ami: AMI_LOGO,
  wealth: WEALTHLINK_LOGO,
  pdf: SPREADI_LOGO,
  'open-api': MIOAPI_LOGO,
};

const UserNemu = (props: { userName: string; userEmail: string }) => {
  const { userName, userEmail } = props;

  const [showLangMenu, setShowLangMenu] = useState<boolean>(false);

  const currentLanguage = user?.preference?.language;
  const currency = user?.preference?.currency;

  const updatePreference = async (lang: UserLanguage) => {
    const json = await Analytics.post(UPDATE_PREFERENCE_URL, {
      language: lang,
      currency,
    });
    if (json.code === 0) {
      window.location.reload();
    }
  };

  const handleChangeLanguage = (lang: UserLanguage) => {
    if (currentLanguage === lang) {
      return;
    }
    updatePreference(lang);
    setShowLangMenu(false);
  };

  const handleClickLogout = () => {
    Analytics.post('/api/user/logout')
      .then(response => {
        if (response.code === 0 || response.code === 27) {
          Toast.success('Log out success!');
          localStorage.clear();
          sessionStorage.clear();
          redirectToApplication('USER', '');
        } else {
          // Toast.error('Log out failed!');
        }
      })
      .catch(() => {
        // Toast.error('Log out failed!');
      });
  };

  const LanguageMenu = (
    <div className={c('mio-language-menu', { show: showLangMenu })}>
      {languageList.map(lang => (
        <div
          key={lang}
          className="mio-lib-main-nav-user-menu-item"
          onClick={() => handleChangeLanguage(lang as UserLanguage)}
        >
          {W(`adm.d.fieldValue.locale.${lang}`)}
        </div>
      ))}
    </div>
  );

  return (
    <div className="mio-lib-main-nav-user-menu">
      <div>
        <div className={'nav-menu-user-name'}>
          <div>{userName}</div>
          <div>{userEmail}</div>
        </div>
      </div>
      <a
        href={`${getUrlToApplication('ADM')}/users/${_.get(
          user,
          'userId',
        )}?from=${window.location.href}`}
        className="mio-lib-main-nav-user-menu-item"
      >
        {W('shared.mainNavNext.editProfile')}
      </a>
      <div
        className="mio-lib-main-nav-user-menu-item mio-menu-language-preference"
        onMouseEnter={() => setShowLangMenu(true)}
        onMouseLeave={() => setShowLangMenu(false)}
      >
        <span>{W('shared.mainNav.preference')}</span>
        <MioIcon type="arrow_right" style={{ fontSize: 12 }} />
        {LanguageMenu}
      </div>
      <div
        className="mio-lib-main-nav-user-menu-item mio-menu-logout"
        onClick={handleClickLogout}
      >
        {W('shared.mainNavNext.logOut')}
      </div>
    </div>
  );
};

const MainNav = (props: Props) => {
  const { children, hideLogo = false } = props;

  const currentApp = useMemo(() => {
    const path = document.location.pathname;
    const currentAppPath = _.get(path.split('/'), '[1]', '');
    return currentAppPath;
  }, []);

  const userEmail = useMemo(() => {
    const _email = `${_.get(user, 'basicInfo.email', '')}`;
    if (!_email.trim()) return '-';
    return _email;
  }, []);

  const userName = useMemo(() => {
    const _name = `${_.get(user, 'basicInfo.firstName', '')} ${_.get(
      user,
      'basicInfo.lastName',
      '',
    )}`;
    if (!_name.trim()) return '-';
    return _name;
  }, []);

  return (
    <div className="mio-lib-main-nav">
      {!hideLogo && (
        <div className={'mio-lib-main-nav-left'}>
          <a href="/">
            <img
              className="mio-logo"
              src={
                user?.preference?.language === 'en-US'
                  ? MIOTECH_LOGO_EN
                  : MIOTECH_LOGO_CN
              }
              alt="MioTech Logo"
            />
          </a>
          <div>
            <div className="mio-lib-main-nav-project">
              {ProjectMap[currentApp] && (
                <img
                  className="mio-lib-main-nav-project-logo"
                  src={ProjectMap[currentApp]}
                  alt={currentApp}
                />
              )}
              {!ProjectMap[currentApp] && currentApp}
            </div>
          </div>
        </div>
      )}
      {/* 项目插入内容 */}
      <div className="mio-lib-main-nav-middle">{children}</div>
      <div className="mio-lib-main-nav-right">
        <MioDropdown
          overlayStyle={{ position: 'fixed' }}
          overlay={<UserNemu userName={userName} userEmail={userEmail} />}
        >
          <div className="nav-menu-user">
            <Avatar name={userName} />
          </div>
        </MioDropdown>
      </div>
    </div>
  );
};

export default MainNav;
